import { useEffect, useRef, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { findPhotoEndpoint, findPhotoUrl } from '../lib/wildFinds';

/** Owner and moderation photos use headers, never credentials embedded in the DOM. */
export default function FindPhoto({ find, index = 0, publicView = false, defer = false,
    placeholder = null, missingLabel, failedLabel, ...props }) {
    const { user } = useAuth();
    const source = findPhotoUrl(find, index);
    const endpoint = findPhotoEndpoint(find, index);
    const [photo, setPhoto] = useState(null);
    const [failedKey, setFailedKey] = useState(null);
    const [entered, setEntered] = useState(!defer || typeof IntersectionObserver === 'undefined');
    const element = useRef(null);
    const key = `${source}:${publicView}:${!!find?.shared}:${!!find?.hidden}:${user?.user_id || ''}:${!!user?.is_guest}`;
    const publicAllowed = publicView && source && find.shared && !find.hidden && !endpoint.startsWith('/finds/admin/');
    const canRead = !publicView && !!user && !user.is_guest;

    useEffect(() => {
        if (!defer || !source || entered) return undefined;
        if (typeof IntersectionObserver === 'undefined') { setEntered(true); return undefined; }
        let active = true;
        const observer = new IntersectionObserver((entries) => {
            if (active && entries.some(entry => entry.isIntersecting || entry.intersectionRatio > 0)) {
                setEntered(true); observer.disconnect();
            }
        }, { rootMargin: '96px', threshold: 0 });
        if (element.current) observer.observe(element.current);
        return () => { active = false; observer.disconnect(); };
    }, [defer, source, entered]);

    useEffect(() => {
        if (!canRead || !source || (defer && !entered)) return undefined;
        const controller = new AbortController();
        let active = true, objectUrl;
        let token = '';
        try { token = localStorage.getItem('session_token') || ''; } catch { /* no local credentials */ }
        api.get(endpoint, {
            responseType: 'blob', signal: controller.signal,
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }).then((response) => {
            if (!active) return;
            objectUrl = URL.createObjectURL(response.data);
            setPhoto({ key, url: objectUrl });
        }).catch((error) => {
            if (active && error?.code !== 'ERR_CANCELED') setPhoto({ key, failed: true });
        });
        return () => { active = false; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [source, endpoint, key, canRead, defer, entered]);

    const failed = failedKey === key || (photo?.key === key && photo.failed);
    const displayed = !failed && (!defer || entered) && (publicView ? publicAllowed && source : canRead && photo?.key === key && photo.url);
    if (displayed) return <img {...props} ref={element} src={displayed} onError={(event) => { setFailedKey(key); props.onError?.(event); }} />;
    const absent = !source || (publicView && !publicAllowed) || (!publicView && !canRead);
    return <span ref={element} data-photo-state={failed ? 'failed' : absent ? 'missing' : 'pending'}
        className={`${props.className || ''} bg-brand-bg2 inline-flex items-center justify-center`}
        style={{ width: props.width, height: props.height, ...props.style }} role="img"
        aria-label={(failed ? failedLabel : absent ? missingLabel : props.alt) || props.alt || ''}>
        {failed ? <ImageOff size={18} className="text-brand-ink3" aria-hidden="true" /> : placeholder}
    </span>;
}
